import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { SharedSidebar } from '../shared-sidebar/shared-sidebar';
import { HelpFabButtonComponent } from '../help-fab-button/help-fab-button';
import { ThemeToggleComponent } from '../theme-toggle/theme-toggle.component';
import { FontSizeToggleComponent } from '../font-size-toggle/font-size-toggle.component';
import { MenuService, MenuItem } from '../../services/menu.service';
import { NotificationService, Notification } from '../../services/notification.service';
import { SegundaViaCertificadoService } from '../../services/dashboard/segunda-via-certificado.service';
import { Subject, takeUntil } from 'rxjs';
import { IAvisosResponse } from '../../interfaces/dashboard/avisos.response';

@Component({
  selector: 'app-shared-layout',
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatSidenavModule,
    MatIconModule,
    MatButtonModule,
    MatBadgeModule,
    MatMenuModule,
    MatDialogModule,
    MatDividerModule,
    SharedSidebar,
    HelpFabButtonComponent,
    ThemeToggleComponent,
    FontSizeToggleComponent,
    RouterOutlet
],
  templateUrl: './shared-layout.html',
  styleUrl: './shared-layout.scss'
})
export class SharedLayout implements OnInit, OnDestroy {
  private _notificationService = inject(NotificationService);
  private _router = inject(Router);
  private _menuService = inject(MenuService);
  private _segundaViaCertificadoService = inject(SegundaViaCertificadoService);
  private _destroy$ = new Subject<void>();

  @Input() currentRoute: string = '';
  @Input() pageTitle: string = '';
  @Output() menuItemClick = new EventEmitter<MenuItem>();
  @Input() avisos: IAvisosResponse[] = [];
  @Output('avisoClick') avisoClickEmtter = new EventEmitter();

  menuItems: MenuItem[] = [];
  sidenavOpened = false; // Default to closed on mobile
  isMobile = false;
  sidenavMode: 'side' | 'over' = 'side';

  notifications: Notification[] = [];
  unreadCount = 0;

  constructor() {
    this.menuItems = this._menuService.getMenuItems();
  }

  ngOnInit() {
    this.checkScreenSize();
    this.loadNotifications();
    this.loadSegundaViaCertificadoMenuItem();
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private loadSegundaViaCertificadoMenuItem() {
    this._segundaViaCertificadoService.getSegundasVias()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (itens) => {
          if (itens && itens.length > 0) {
            this.addSegundaViaCertificadoMenuItem();
          }
        },
        error: () => {
          this.removeSegundaViaCertificadoMenuItem();
        }
      });
  }

  private addSegundaViaCertificadoMenuItem() {
    const route = 'dashboard/segunda-via-certificado';
    if (this.menuItems.some(item => item.route === route)) return;

    const item: MenuItem = {
      label: 'Segunda via do certificado',
      icon: 'file_copy',
      route
    };
    const indexCertificados = this.menuItems.findIndex(i => i.label === 'Certificados');
    const itens = [...this.menuItems];
    itens.splice(indexCertificados >= 0 ? indexCertificados + 1 : itens.length, 0, item);
    this.menuItems = itens;
  }

  private removeSegundaViaCertificadoMenuItem() {
    const route = 'dashboard/segunda-via-certificado';
    if (this.menuItems.some(item => item.route === route)) {
      this.menuItems = this.menuItems.filter(item => item.route !== route);
    }
  }

  private loadNotifications() {
    this._notificationService.notifications$
      .pipe(takeUntil(this._destroy$))
      .subscribe(notifications => {
        this.notifications = notifications;
        // this.unreadCount = this._notificationService.unreadCount;
        this.unreadCount = this.avisos.length;
      });
  }

  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    this.isMobile = window.innerWidth <= 768;

    if (this.isMobile) {
      this.sidenavMode = 'over';
      this.sidenavOpened = false; // Always closed on mobile by default
    } else {
      this.sidenavMode = 'side';
      this.sidenavOpened = true; // Open by default on desktop
    }
  }

  onMenuItemClick(item: MenuItem) {
    if (item.externalUrl) {
      // Open external URL in new tab
      window.open(item.externalUrl, '_blank');
    } else if (item.route && item.route !== this.currentRoute) {
      this._router.navigate([item.route]);
      // Close sidenav on mobile after navigation
      if (this.isMobile) {
        this.sidenavOpened = false;
      }
    }
    this.menuItemClick.emit(item);
  }

  toggleSidenav() {
    this.sidenavOpened = !this.sidenavOpened;
  }

  goHome() {
    this._router.navigate(['/dashboard/inicio']);
    // Close sidenav on mobile after navigation
    if (this.isMobile) {
      this.sidenavOpened = false;
    }
  }

  logout() {
    // Navigate to login page
    this._router.navigate(['/login']);
  }

  markNotificationAsRead(notification: Notification) {
    this._notificationService.markAsRead(notification.id);
  }

  markAllNotificationsAsRead() {
    this._notificationService.markAllAsRead();
  }

  getNotificationIcon(type: string): string {
    switch (type) {
      case 'info': return 'info';
      case 'warning': return 'warning';
      case 'success': return 'check_circle';
      case 'error': return 'error';
      default: return 'notifications';
    }
  }

  getNotificationColor(type: string): string {
    switch (type) {
      case 'info': return 'primary';
      case 'warning': return 'accent';
      case 'success': return 'primary';
      case 'error': return 'warn';
      default: return 'primary';
    }
  }

  clickNotification(){

    this.avisoClickEmtter.emit();
  }
}
